<template>
  <div>
    <q-table
      :pagination="pagination"
      table-header-class="bg-blue-brand text-white"
      :rows="rows"
      :columns="columns"
      :row-key="rowKey"
      :sort-method="sortMethod"
      :hide-bottom="hideBottom"
      dense
      :loading="loading"
      :no-data-label="noDataLabel"
      v-on="rowClickListeners(rowOpensChart, onRowClick)"
      @update:pagination="onUpdatePagination"
    >
      <template #body-cell-graph-data="props">
        <q-td :props="props">
          <template v-if="isManualPassage(props.row)">
            <q-badge color="grey" class="q-mr-xs">
              {{ getWaveformLabel('H') }}
            </q-badge>
          </template>
          <span v-else-if="(props.row.passageWaveforms || []).length === 1">
            <q-btn
              v-for="wf in props.row.passageWaveforms"
              :key="wf.passageWaveformId"
              size="sm"
              class="q-mr-xs btn-brand"
              dense
              icon="show_chart"
              :label="getWaveformLabel(wf.passageWaveformType)"
              @click="onShowChart(props.row, wf)"
            ></q-btn>
          </span>
          <span v-else-if="(props.row.passageWaveforms || []).length > 1">
            <q-btn-dropdown
              size="sm"
              class="q-mr-xs btn-brand"
              dense
              icon="show_chart"
            >
              <q-list dense>
                <q-item
                  v-for="wf in props.row.passageWaveforms"
                  :key="wf.passageWaveformId"
                  v-close-popup
                  clickable
                  @click="onShowChart(props.row, wf)"
                >
                  <q-item-section>
                    <q-item-label>{{
                      getWaveformLabel(wf.passageWaveformType)
                    }}</q-item-label>
                  </q-item-section>
                </q-item>
              </q-list>
            </q-btn-dropdown>
          </span>
          <span v-if="!props.row.passageId">[manual passage] </span>
          <q-btn
            v-if="
              !props.row.passageId &&
              (getuser() == 'admin' || getuser() == 'superadmin')
            "
            size="sm"
            class="btn-brand"
            dense
            color="blue-brand"
            icon="edit"
            @click="onConfirmEditManualPassage(props.row)"
          ></q-btn>
        </q-td>
      </template>
      <template #body-cell-uid="props">
        <q-td :props="props">{{ props.value }}</q-td>
      </template>
      <template #body-cell-verify-data="props">
        <q-td :props="props">
          <q-btn
            v-if="props.row.passageId"
            size="sm"
            class="btn-brand"
            dense
            :color="isAnyWaveformVerified(props.row) ? 'green' : 'red'"
            :icon="
              isAnyWaveformVerified(props.row)
                ? 'check_circle_outline'
                : 'block'
            "
            @click="onVerifyPassage(props.row)"
          >
          </q-btn>

          <q-btn
            v-if="
              !props.row.passageId &&
              (getuser() == 'admin' || getuser() == 'superadmin')
            "
            size="sm"
            class="btn-brand"
            dense
            color="red"
            icon="delete"
            @click="onConfirmDeleteManualPassage(props.row)"
          >
          </q-btn>
        </q-td>
      </template>

      <template #body-cell-machine-learning-data="props">
        <q-td :props="props">
          <MLBadge
            v-for="wf in getMLWaveforms(props.row)"
            :key="wf.passageWaveformId"
            :type="wf.passageWaveformType"
            :prediction="wf.mlPrediction"
            :confidence="wf.mlConfidence"
            :results="wf.machineLearningResults"
            :tooltip="
              mlOpensChart
                ? `View ${getWaveformLabel(wf.passageWaveformType)} waveform`
                : undefined
            "
            :data-testid="mlOpensChart ? 'view-passages-ml-result' : undefined"
            @click="onMLBadgeClick(props.row, wf)"
          />
        </q-td>
      </template>
    </q-table>

    <WaveformVerifyDialog
      ref="waveformVerifyDialog"
      @confirm="onWaveformVerifyConfirm"
      @cancel="onWaveformVerifyCancel"
    />
  </div>
</template>

<script lang="ts">
/**
 * EN-1851: the passages table of the View Passages dialog
 * (PipelineSectionMarkerPassagesDialog.vue), shared with the tracksheet run
 * passages section: waveform chart buttons, verify / unverify (admin and
 * superadmin only), ML badges and next / previous passage in the chart.
 *
 * The parent owns the rows (fetching, filtering, live refresh). A row's marker
 * is the pipelineSectionMarkerId / markerName props when given (the dialog
 * shows one marker), otherwise the row's own fields (run passages rows).
 */
import { defineComponent, PropType } from 'vue';
import { EventBus } from '@/utils/EventBus';
import { IPassage } from '@/models/Passage';
import {
  WaveformTypeLabel,
  IPassageWaveformSummary,
} from '@/models/PassageWaveform';
import {
  isAnyWaveformVerified,
  getAvailableWaveforms,
} from '@/utils/WaveformUtils';
import PigRunService from '@/services/PigRunService';
import { PassageVerificationRequest } from '@/models/ReportManager/PassageVerificationRequest';
import { validatePassageVerification } from '@/utils/PassageValidation';
import WaveformVerifyDialog from '@/components/WaveformVerifyDialog.vue';
import MLBadge from '@/components/MLBadge.vue';
import {
  getMLWaveforms,
  isControlClick,
  isManualPassage,
  rowChartWaveform,
  rowClickListeners,
} from '@/utils/PassageTableRows';

/** The View Passages dialog's columns (the default). */
export const PASSAGE_TABLE_COLUMNS = [
  {
    name: 'passageTimeStamp',
    label: 'Time (UTC)',
    field: 'passageTimeStamp',
    align: 'left' as const,
    sortable: true,
  },
  {
    name: 'uid',
    label: 'UID',
    field: 'uid',
    align: 'left' as const,
    sortable: true,
  },
  {
    name: 'detectionCount',
    label: 'Det Count',
    field: 'detectionCount',
    align: 'left' as const,
    sortable: true,
  },
  {
    name: 'graph-data',
    label: 'Waveforms',
    field: 'graph-data',
    align: 'left' as const,
  },
  {
    name: 'verify-data',
    label: 'Verify',
    field: 'verify-data',
    align: 'center' as const,
  },
  {
    name: 'machine-learning-data',
    label: 'ML',
    field: 'machine-learning-data',
    align: 'center' as const,
  },
];

type PassageRow = IPassage & {
  pipelineSectionMarkerId?: number | null;
  markerName?: string | null;
};

export default defineComponent({
  name: 'PassagesTable',
  components: { WaveformVerifyDialog, MLBadge },
  props: {
    rows: { type: Array as PropType<PassageRow[]>, required: true },
    columns: {
      type: Array as PropType<any[]>,
      default: () => PASSAGE_TABLE_COLUMNS,
    },
    rowKey: {
      type: [String, Function] as PropType<string | ((row: any) => string)>,
      default: 'passageId',
    },
    /** q-table sort-method; unset keeps Quasar's own sort (the dialog). */
    sortMethod: {
      type: Function as PropType<
        (rows: readonly any[], sortBy: string, descending: boolean) => any[]
      >,
      default: undefined,
    },
    loading: { type: Boolean, default: false },
    noDataLabel: { type: String, default: 'No passages received' },
    pagination: { type: Object as PropType<any>, default: undefined },
    /** setPassageDialogSource value when a chart is opened from this table. */
    dialogSource: { type: String, default: 'marker_passages' },
    pigRunId: { type: Number as PropType<number | null>, default: null },
    pipelineSectionMarkerId: {
      type: Number as PropType<number | null>,
      default: null,
    },
    markerName: { type: String as PropType<string | null>, default: null },
    /** EN-1851: a row click opens its first waveform (no Waveforms column). */
    rowOpensChart: { type: Boolean, default: false },
    /** EN-1851: no pagination bar (the tracksheet popover's few rows). */
    hideBottom: { type: Boolean, default: false },
    /**
     * EN-1563: clicking an ML badge opens that waveform's chart. Only the
     * View Passages dialog sets it; the tracksheet views keep inert badges.
     */
    mlOpensChart: { type: Boolean, default: false },
    /** Whether the chart's next / previous passage steps through these rows. */
    active: { type: Boolean, default: true },
  },
  emits: ['update:pagination'],
  data() {
    return {
      pendingVerifyPassage: null as PassageRow | null,
    };
  },
  mounted() {
    EventBus.on('switch-passage', this.handleSwitchPassage as any);
  },
  beforeUnmount() {
    EventBus.off('switch-passage', this.handleSwitchPassage as any);
  },
  methods: {
    /** EN-1510: a verify left over from a previous open must not carry over. */
    resetPendingVerify() {
      this.pendingVerifyPassage = null;
    },
    onUpdatePagination(pagination: any) {
      this.$emit('update:pagination', pagination);
    },
    markerIdFor(row: PassageRow): number | null {
      return (
        this.pipelineSectionMarkerId ?? row.pipelineSectionMarkerId ?? null
      );
    },
    markerNameFor(row: PassageRow): string {
      return this.markerName ?? row.markerName ?? '';
    },
    getuser(): string {
      return this.$store.state.user?.role || '';
    },
    isManualPassage(row: PassageRow): boolean {
      return isManualPassage(row);
    },
    getWaveformLabel(type: string): string {
      return WaveformTypeLabel[type] || type;
    },
    isAnyWaveformVerified(passage: PassageRow): boolean {
      return isAnyWaveformVerified(passage);
    },
    rowClickListeners(
      opensChart: boolean,
      onRowClick: (...args: any[]) => void
    ) {
      return rowClickListeners(opensChart, onRowClick);
    },
    getMLWaveforms(row: PassageRow): IPassageWaveformSummary[] {
      return getMLWaveforms(row);
    },
    onShowChart(row: PassageRow, wf: IPassageWaveformSummary) {
      this.$store.dispatch('setPassageDialogSource', this.dialogSource);

      const passageCopy = {
        ...row,
        requestedWaveformId: wf.passageWaveformId,
        pipelineSectionMarkerId: this.markerIdFor(row),
        pigRunId: this.pigRunId,
      };
      this.$store.dispatch('addPassageIdsToUpdate', [wf.passageWaveformId]);
      this.$store.dispatch('passageChartShow', passageCopy);
    },
    /** EN-1563: an ML badge opens its waveform only where mlOpensChart is set. */
    onMLBadgeClick(row: PassageRow, wf: IPassageWaveformSummary) {
      if (this.mlOpensChart) this.onShowChart(row, wf);
    },
    onRowClick(event: Event, row: PassageRow) {
      // The row's own buttons (verify, ML) keep their action.
      if (isControlClick(event?.target as Element | null)) return;
      const wf = rowChartWaveform(row);
      if (wf) this.onShowChart(row, wf);
    },
    onConfirmEditManualPassage(_row: any) {},
    tagWithMarker(passage: PassageRow) {
      const markerName = this.markerNameFor(passage);
      const markerId = this.markerIdFor(passage);
      (passage as any).markerName = markerName;
      (passage as any).pipelineSectionMarkerId = markerId;
      (passage as any).pigRunId = this.pigRunId;
    },
    async onVerifyPassage(passage: PassageRow) {
      if (!(this.getuser() === 'admin' || this.getuser() === 'superadmin'))
        return;

      const waveforms = getAvailableWaveforms(passage);
      if (waveforms.length === 0) return;

      this.tagWithMarker(passage);

      // If any waveform is verified, unverify all
      if (isAnyWaveformVerified(passage)) {
        this.$store.dispatch('unverifyAllPassageWaveforms', { passage });
        EventBus.emit('passage-verified', { passage });
        return;
      }

      // Check verified passage and timestamp ordering
      try {
        const check = await validatePassageVerification(
          this.markerIdFor(passage)!,
          this.pigRunId,
          passage.passageTimeStamp
        );
        if (!check.valid) {
          this.$q.notify({
            type: check.warning ? 'warning' : 'negative',
            timeout: check.warning ? 0 : undefined,
            actions: check.warning
              ? [{ icon: 'close', color: 'white', round: true }]
              : undefined,
            message: (check.warning ?? check.error)!,
          });
          return;
        }
      } catch (error) {
        console.error('Passage verification validation failed:', error);
        return;
      }

      // Single waveform: verify directly
      if (waveforms.length === 1) {
        const passageWaveformId = waveforms[0].value;
        if (!passage.uid) {
          // Manual passages have no uid — call API directly
          await this.verifyManualPassage(passage, passageWaveformId);
        } else {
          this.$store.dispatch('verifyPassage', {
            passage,
            passageWaveformId,
          });
          EventBus.emit('passage-verified', { passage });
        }
        return;
      }

      // Multiple waveforms: show preview selection dialog
      this.pendingVerifyPassage = passage;
      const firstWfId = waveforms[0].value;
      (this.$refs.waveformVerifyDialog as any).open(passage.uid!, firstWfId);
    },
    onWaveformVerifyConfirm(passageWaveformId: number) {
      if (this.pendingVerifyPassage) {
        this.tagWithMarker(this.pendingVerifyPassage);
        this.$store.dispatch('verifyPassage', {
          passage: this.pendingVerifyPassage,
          passageWaveformId,
        });
        EventBus.emit('passage-verified', {
          passage: this.pendingVerifyPassage,
        });
      }
      this.pendingVerifyPassage = null;
    },
    onWaveformVerifyCancel() {
      this.pendingVerifyPassage = null;
    },
    async verifyManualPassage(passage: PassageRow, passageWaveformId: number) {
      const wf = (passage.passageWaveforms || []).find(
        (w) => w.passageWaveformId === passageWaveformId
      );
      if (!wf) return;
      const newVerified = !wf.isVerified;
      try {
        await PigRunService.setPassageWaveformVerification(
          new PassageVerificationRequest(passageWaveformId, newVerified)
        );
        wf.isVerified = newVerified;
        if (this.pigRunId != null) {
          this.$store.dispatch('refreshSinglePigRun', {
            pigRunId: this.pigRunId,
            force: true,
          });
        }
        EventBus.emit('passage-verified', { passage });
      } catch (error) {
        console.error('Failed to verify manual passage:', error);
        this.$q.notify({
          type: 'negative',
          message: 'Unable to verify passage. Please try again.',
        });
      }
    },
    onConfirmDeleteManualPassage(_row: any) {},
    handleSwitchPassage({
      passage,
      direction,
      type,
    }: {
      passage: any;
      direction: 'next' | 'previous';
      type: string;
    }) {
      if (!this.active) return;
      const currentWaveformId =
        passage.requestedWaveformId || passage.passageId;

      const rows = this.rows;
      const hasWaveform = (psg: any) =>
        (psg.passageWaveforms || []).some(
          (wf: any) => wf.passageWaveformId === currentWaveformId
        );
      // EN-1851: run passages rows repeat a passage once per marker; step
      // from the row the chart was opened on (the dialog has one marker).
      let currentIndex = rows.findIndex(
        (psg: any) =>
          hasWaveform(psg) &&
          this.markerIdFor(psg) === (passage.pipelineSectionMarkerId ?? null)
      );
      if (currentIndex === -1) currentIndex = rows.findIndex(hasWaveform);

      if (currentIndex === -1) return;

      const inc = direction === 'next' ? 1 : -1;
      const nextIndex = (currentIndex + inc + rows.length) % rows.length;
      const nextRow = rows[nextIndex];
      const nextWf =
        (nextRow.passageWaveforms || []).find(
          (wf: any) => wf.passageWaveformType === type
        ) || (nextRow.passageWaveforms || [])[0];
      if (nextWf) {
        this.onShowChart(nextRow, nextWf);
      }
    },
  },
});
</script>
